import type { ReactNode } from 'react';
import type { ScreenId } from '../App';
import type { AuthUser } from '../api/types';
import { Avatar } from './Avatar';

const SettingsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="2.2" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M8 1.5v1.3M8 13.2v1.3M1.5 8h1.3M13.2 8h1.3M3.4 3.4l.9.9M11.7 11.7l.9.9M3.4 12.6l.9-.9M11.7 4.3l.9-.9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const DashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity=".9" />
    <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity=".5" />
    <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity=".5" />
    <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity=".9" />
  </svg>
);
const MapIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 12v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="8" cy="7" r="2" fill="currentColor" />
  </svg>
);
const RouteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="3.5" cy="12.5" r="2" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="12.5" cy="3.5" r="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M5.5 12.5h4a2.5 2.5 0 000-5h-3a2.5 2.5 0 010-5h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
const CalIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="1.5" y="3" width="13" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M5 1.5v3M11 1.5v3M1.5 7h13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
const EngIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5" />
    <path d="M2 14c0-3.314 2.686-5 6-5s6 1.686 6 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
const ClientsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M2 13.5V12c0-1.1.9-2 2-2h3c1.1 0 2 .9 2 2v1.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="5.5" cy="5" r="2.2" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M10.5 13.5V12c0-.9.7-1.7 1.6-1.9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="12" cy="5.5" r="1.8" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);
const RepIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="1" y="9" width="3" height="6" rx="1" fill="currentColor" />
    <rect x="5.5" y="5" width="3" height="10" rx="1" fill="currentColor" />
    <rect x="10" y="1" width="3" height="14" rx="1" fill="currentColor" />
    <path d="M1 6l4-3 4 2 5-4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Все NAV-элементы с указанием ролей, которым они показываются.
// ENGINEER = полные права DISPATCHER: Обзор/Карта/Расписание/Клиенты.
type Role = AuthUser['role'];
const NAV: Array<{ id: ScreenId; label: string; icon: ReactNode; roles: Role[]; superAdminOnly?: boolean }> = [
  { id: 'dashboard', label: 'Обзор', icon: <DashIcon />, roles: ['ADMIN', 'DISPATCHER', 'ENGINEER'] },
  { id: 'map', label: 'Карта', icon: <MapIcon />, roles: ['ADMIN', 'DISPATCHER', 'ENGINEER'] },
  // «Маршруты» — передвижения инженеров по GPS, только ADMIN (как и «Отчёты»).
  { id: 'routes', label: 'Маршруты', icon: <RouteIcon />, roles: ['ADMIN'] },
  { id: 'schedule', label: 'Расписание', icon: <CalIcon />, roles: ['ADMIN', 'DISPATCHER', 'ENGINEER'] },
  // «Инженеры» и «Отчёты» — только ADMIN (Спринт 8). DISPATCHER/ENGINEER их не видят.
  { id: 'engineers', label: 'Инженеры', icon: <EngIcon />, roles: ['ADMIN'] },
  { id: 'clients', label: 'Клиенты', icon: <ClientsIcon />, roles: ['ADMIN', 'DISPATCHER', 'ENGINEER'] },
  { id: 'reports', label: 'Отчёты', icon: <RepIcon />, roles: ['ADMIN'] },
  // «Настройки» — только SUPER_ADMIN (ADMIN с битрикс-ID в superAdminBitrixIds).
  { id: 'settings', label: 'Настройки', icon: <SettingsIcon />, roles: ['ADMIN'], superAdminOnly: true },
];

interface SidebarProps {
  active: ScreenId;
  onNav(id: ScreenId): void;
  /** Если undefined — кнопка «+ Новый выезд» скрыта. */
  onAdd?(): void;
  onLogout?(): void;
  user: AuthUser;
  /** Свёрнутый режим: только иконки, подписи — в tooltip. */
  collapsed?: boolean;
  onToggleCollapse?(): void;
}

// Стрелка «свернуть/развернуть»: в свёрнутом режиме разворачивается вправо.
const CollapseIcon = ({ collapsed }: { collapsed: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    style={{ transform: collapsed ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }}
  >
    <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function Sidebar({ active, onNav, onAdd, onLogout, user, collapsed = false, onToggleCollapse }: SidebarProps) {
  return (
    <div
      style={{
        width: collapsed ? 60 : 210,
        background: '#0f172a',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        flexShrink: 0,
        borderRight: '1px solid rgba(255,255,255,0.05)',
        transition: 'width 0.15s',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: collapsed ? '18px 0 14px' : '20px 12px 16px 18px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: 8,
        }}
      >
        {!collapsed && (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#60a5fa', letterSpacing: '1.5px', marginBottom: 2 }}>FIELDDESK</div>
            <div style={{ fontSize: 11, color: '#475569', whiteSpace: 'nowrap' }}>Планирование выездов</div>
          </div>
        )}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            title={collapsed ? 'Развернуть панель' : 'Свернуть панель'}
            aria-label={collapsed ? 'Развернуть панель' : 'Свернуть панель'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              flexShrink: 0,
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 7,
              color: '#94a3b8',
            }}
          >
            <CollapseIcon collapsed={collapsed} />
          </button>
        )}
      </div>

      <nav style={{ padding: collapsed ? '12px 8px' : '12px 10px', flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {NAV.filter((it) => it.roles.includes(user.role) && (!it.superAdminOnly || user.isSuperAdmin === true)).map((item) => {
          const isActive = active === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onNav(item.id)}
              title={collapsed ? item.label : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: 10,
                width: '100%',
                padding: collapsed ? '9px 0' : '9px 12px',
                background: isActive ? 'rgba(37,99,235,0.25)' : 'transparent',
                border: isActive ? '1px solid rgba(37,99,235,0.35)' : '1px solid transparent',
                borderRadius: 7,
                color: isActive ? '#93c5fd' : '#64748b',
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                marginBottom: 3,
                textAlign: 'left',
                transition: 'all 0.12s',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ opacity: isActive ? 1 : 0.7, display: 'inline-flex' }}>{item.icon}</span>
              {!collapsed && item.label}
            </button>
          );
        })}

      </nav>

      {onAdd && (
        <div style={{ padding: collapsed ? '12px 8px' : '12px 10px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button
            onClick={onAdd}
            title={collapsed ? 'Новый выезд' : undefined}
            style={{
              width: '100%',
              padding: '10px',
              background: '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              fontSize: 13,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
            {!collapsed && ' Новый выезд'}
          </button>
        </div>
      )}

      <div
        title={collapsed ? `${user.fullName} · ${user.role}` : undefined}
        style={{
          padding: collapsed ? '12px 0' : '12px 14px',
          borderTop: '1px solid rgba(255,255,255,0.07)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 10,
        }}
      >
        <Avatar initials={user.initials} color={user.color} size={28} />
        {!collapsed && (
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              color: '#e2e8f0',
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {user.fullName}
          </div>
          <div style={{ color: '#64748b', fontSize: 10 }}>{user.role}</div>
        </div>
        )}
        {onLogout && !collapsed && (
          <button
            onClick={onLogout}
            title="Выйти"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#64748b',
              fontSize: 14,
              padding: 4,
            }}
          >
            ⎋
          </button>
        )}
      </div>
    </div>
  );
}
